import React, { useState, useEffect } from 'react';
import { Row, Col, Select, Button, message, Alert, Input, Card, Typography, Tag } from 'antd';
import HeartLoader from '../../../../components/shared/HeartLoader';
import { CalendarOutlined, EnvironmentOutlined, SwapOutlined, CheckCircleOutlined } from '@ant-design/icons';
import moment from 'moment';
import VisitService from '../../../../services/VisitService';
import { 
  SectionTitle,
  FormSection
} from '../styled';

const { Option } = Select;
const { TextArea } = Input;
const { Text } = Typography;

/**
 * EventChangeTab - Tab component for changing the event/booking associated with a visit
 * @param {Object} patient - Patient data including eventInfo
 * @param {Function} onEventChanged - Callback when event is successfully changed
 */
const EventChangeTab = ({ patient, onEventChanged }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [reason, setReason] = useState('');

  // Current event info from patient data
  const currentEventInfo = patient?.eventInfo || {};
  const currentEventId = patient?.eventId || currentEventInfo?.eventId;

  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    setLoading(true);
    try {
      // Use dedicated endpoint that fetches all past events + up to 10 future events
      const response = await VisitService.getEventsForReassignment();
      
      if (response?.success && response?.data?.length) {
        setEvents(response.data);
      } else {
        setEvents([]);
      }
    } catch (error) {
      console.error('Error loading events:', error);
      message.error('Failed to load events');
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChangeEvent = async () => {
    if (!selectedEventId) {
      message.warning('Please select an event');
      return;
    }

    if (selectedEventId === currentEventId) {
      message.info('This is already the current event');
      return;
    }

    // Get patient visit ID - try multiple possible sources
    const visitId = patient?.visitId || patient?._id || patient?.visit?._id;
    
    if (!visitId) {
      message.error('Unable to identify visit. Please refresh and try again.');
      return;
    }

    setSaving(true);
    try {
      const response = await VisitService.changeVisitEvent(visitId, selectedEventId, reason);
      
      if (response?.success) {
        const readingsUpdated = response.data?.medicalReadingsUpdated || 0;
        if (readingsUpdated > 0) {
          message.success(`Event changed successfully. Updated ${readingsUpdated} medical reading${readingsUpdated > 1 ? 's' : ''} to reflect the new event.`);
        } else {
          message.success('Event changed successfully');
        }
        setSelectedEventId(null);
        setReason('');
        
        // Notify parent to refresh data
        if (onEventChanged) {
          onEventChanged(response.data);
        }
      } else {
        throw new Error(response?.message || 'Failed to change event');
      }
    } catch (error) {
      console.error('Error changing event:', error);
      message.error(error.response?.data?.message || error.message || 'Failed to change event');
    } finally {
      setSaving(false);
    }
  };

  const formatEventLabel = (event) => {
    const date = moment(event.startDate).format('MMM D, YYYY');
    const isToday = moment(event.startDate).isSame(moment(), 'day');
    const isFuture = moment(event.startDate).isAfter(moment());
    const isCurrent = event._id === currentEventId;
    
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0' }}>
        <CalendarOutlined style={{ color: isToday ? '#52c41a' : isFuture ? '#1890ff' : '#8c8c8c' }} />
        <span style={{ fontWeight: isToday ? 600 : 400 }}>
          {event.title}
        </span>
        <span style={{ color: '#8c8c8c' }}>•</span>
        <span style={{ color: '#595959', fontSize: '13px' }}>{date}</span>
        {event.location && (
          <>
            <EnvironmentOutlined style={{ color: '#8c8c8c', marginLeft: '4px' }} />
            <span style={{ color: '#595959', fontSize: '13px' }}>{event.location}</span>
          </>
        )}
        {isToday && <Tag color="green" style={{ marginLeft: '8px' }}>Today</Tag>}
        {isCurrent && <Tag color="blue" style={{ marginLeft: '8px' }}>Current</Tag>}
      </div>
    );
  };

  return (
    <>
      <FormSection>
        <SectionTitle>
          <CalendarOutlined />
          Current Event
        </SectionTitle>

        <Card 
          style={{ 
            marginBottom: 24, 
            backgroundColor: '#fafafa',
            border: '1px solid #e8e8e8'
          }}
        >
          <Row gutter={16} align="middle">
            <Col>
              <CalendarOutlined style={{ fontSize: 24, color: '#1890ff' }} />
            </Col>
            <Col flex="auto">
              <div>
                <Text strong style={{ fontSize: 16 }}>
                  {currentEventInfo.eventTitle || 'No event assigned'}
                </Text>
              </div>
              {currentEventInfo.eventLocation && (
                <div style={{ marginTop: 4 }}>
                  <EnvironmentOutlined style={{ color: '#8c8c8c', marginRight: 6 }} />
                  <Text type="secondary">{currentEventInfo.eventLocation}</Text>
                </div>
              )}
            </Col>
            {currentEventId && (
              <Col>
                <Tag color="blue">
                  <CheckCircleOutlined /> Active
                </Tag>
              </Col>
            )}
          </Row>
        </Card>

        <SectionTitle>
          <SwapOutlined />
          Change Event
        </SectionTitle>

        <Alert
          message="Change Member's Event"
          description="Use this to reassign the member to a different event if they were checked in to the wrong one. This will update their visit record AND any medical readings from this visit to be associated with the correct event."
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <HeartLoader size="32px" text="Loading events..." padding="0" />
          </div>
        ) : (
          <>
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                  Select New Event
                </label>
                <Select
                  style={{ width: '100%' }}
                  size="large"
                  placeholder="Choose an event to transfer to..."
                  value={selectedEventId}
                  onChange={setSelectedEventId}
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) => {
                    const event = events.find(e => e._id === option.value);
                    if (!event) return false;
                    const searchStr = `${event.title} ${event.location}`.toLowerCase();
                    return searchStr.includes(input.toLowerCase());
                  }}
                  notFoundContent={events.length === 0 ? 'No events found' : null}
                >
                  {events.map(event => (
                    <Option 
                      key={event._id} 
                      value={event._id}
                      disabled={event._id === currentEventId}
                    >
                      {formatEventLabel(event)}
                    </Option>
                  ))}
                </Select>
              </Col>
            </Row>

            <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
              <Col span={24}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                  Reason for Change (Optional)
                </label>
                <TextArea
                  rows={3}
                  placeholder="Enter the reason for changing the event (e.g., 'Member was at the Pink Goes Red event, not Health Hive')"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  style={{ borderRadius: 8 }}
                />
              </Col>
            </Row>

            <Row style={{ marginTop: 24 }}>
              <Col>
                <Button
                  type="primary"
                  size="large"
                  icon={<SwapOutlined />}
                  onClick={handleChangeEvent}
                  loading={saving}
                  disabled={!selectedEventId || selectedEventId === currentEventId}
                  style={{ 
                    height: 48,
                    paddingLeft: 24,
                    paddingRight: 24,
                    borderRadius: 8
                  }}
                >
                  Change Event
                </Button>
              </Col>
            </Row>
          </>
        )}
      </FormSection>
    </>
  );
};

export default EventChangeTab;
